import React from "react";
import { makeStyles } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import CustomModal from "common/components/CustomModal/CustomModal";
import {
	ModalHeader,
	ModalBody,
	ModalPanel,
} from "common/components/CustomModal/Modal/ModalFrame";
import ModalHelper from "common/helpers/modalHelper";
import TrophyList from "common/components/TrophyList/TrophyList";
import ViewToggle from "common/components/ViewToggle/ViewToggle";
import { CONTROL_HEIGHT } from "common/components/EventControls/eventControlStyles";
import { sortAwards, TrophySortKeys } from "common/helpers/trophyHelper";
import { Colors } from "styles/Colors";

const useStyles = makeStyles(() => ({
	header: {
		"& h3": {
			marginBottom: 6,
		},
	},
	controls: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		marginTop: 2,
		marginBottom: 10,
	},
	direction: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: CONTROL_HEIGHT,
		height: CONTROL_HEIGHT,
		padding: 0,
		margin: 0,
		boxSizing: "border-box",
		borderRadius: 999,
		border: "1px solid rgba(255, 255, 255, 0.16)",
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		color: Colors.primaryDark,
		cursor: "pointer",
		outline: "none",
		transition: "color 200ms ease, background-color 200ms ease",

		"&:hover": {
			color: Colors.primary,
			backgroundColor: "rgba(255, 255, 255, 0.06)",
		},

		"&:active": {
			backgroundColor: "rgba(255, 255, 255, 0.10)",
		},

		"&:focus-visible": {
			boxShadow: `0 0 0 2px ${Colors.pickstop}`,
		},

		"& svg": {
			width: 18,
			height: 18,
		},
	},
	scroller: {
		maxHeight: "max(320px, calc(100vh - 200px))",
		overflowY: "auto",
		overflowX: "hidden",
	},
	faded: {
		maskImage: "linear-gradient(to bottom, transparent 0, #000 24px)",
	},
}));

export default function TrophySelectorModal(props) {
	const styles = useStyles();
	/** @type {Array<any>} */
	const exceptThese = props.except;
	const [sort, setSort] = React.useState({
		key: TrophySortKeys.date,
		descending: true,
	});
	const [scrolled, setScrolled] = React.useState(false);
	const onSelect = props.onSelect;
	if (!onSelect) {
		console.error(
			"You are using a Trophy Selector Modal without an onSelect callback."
		);
	}

	const awards = React.useMemo(() => {
		const selectable = props.awards.filter((a) => {
			return (
				!exceptThese ||
				!exceptThese.find((e) => e.eventId === a.eventId && e.id === a.id)
			);
		});

		return sortAwards(selectable, sort);
	}, [props.awards, exceptThese, sort]);

	const onScroll = (e) => {
		const isScrolled = e.target.scrollTop > 0;
		if (isScrolled !== scrolled) {
			setScrolled(isScrolled);
		}
	};

	return (
		<CustomModal>
			<ModalPanel>
				<ModalHeader
					className={styles.header}
					onClose={() => ModalHelper.closeModal()}
				>
					{props.header ?? "Select a Trophy"}
				</ModalHeader>
				<ModalBody>
					{awards.length > 1 && (
						<div className={styles.controls}>
							<ViewToggle
								value={sort.key}
								items={[
									{ id: TrophySortKeys.date, text: "Date" },
									{ id: TrophySortKeys.tier, text: "Tier" },
								]}
								onChange={(key) => setSort({ ...sort, key })}
							/>
							<button
								type="button"
								className={styles.direction}
								title={sort.descending ? "Descending" : "Ascending"}
								onClick={() =>
									setSort({ ...sort, descending: !sort.descending })
								}
							>
								{sort.descending ? (
									<KeyboardArrowDownIcon />
								) : (
									<KeyboardArrowUpIcon />
								)}
							</button>
						</div>
					)}
					<div
						className={styles.scroller + (scrolled ? " " + styles.faded : "")}
						onScroll={onScroll}
					>
						<TrophyList awards={awards} onClick={onSelect} />
					</div>
				</ModalBody>
			</ModalPanel>
		</CustomModal>
	);
}
